import React, {Fragment, useContext, useEffect} from "react";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {createFormFieldConfig, useForm} from "@/hooks/useForm";
import {useAlertStore} from "@/store/alert_store";
import {AppSetting, AppSettingDataType} from "@/api/dto/dto";
import {useMutation, useQuery} from "@tanstack/react-query";
import {ToastType} from "patient-ping-remedy/packages/toast";
import {AxiosError} from "axios";
import {GENERICERROR} from "@manual_encounters/constants/BannerMessages";
import Helpers from "@/helpers/helpers";
import Validate from "@/helpers/validate";
import {SmallTypography} from "patient-ping-remedy/packages/typography";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import {css} from "@emotion/css";
import DeploymentFooter from "@/components/DeploymentFooter";
import Checkbox from "patient-ping-remedy/packages/checkbox";
import ValidatedInput from "@common/ValidatedInput";
import Loading from "@common/Loading";
import {queryClient} from "@/App";

export default function AppSettingsTab() {
  const {carecoApi} = useContext(CarecoApiContext);
  const {isValid, form, setInitialForm, handleChange, handleSubmit, isFormChanged} = useForm(submit);
  const [inFlight, setInFlight] = React.useState(false);
  const {addAlert} = useAlertStore();

  const {data : appSettings, isLoading} = useQuery({
    queryFn: () => carecoApi?.getAppSettings(),
    queryKey: ["app-settings"],
    enabled: !!carecoApi,
  });

  const updateAppSettingsMutation = useMutation({
    mutationFn: async (appSettings: AppSetting[]) => carecoApi?.postAppSettings(appSettings),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["app-settings"]}).then();
      addAlert({content: 'App settings updated successfully.', type: ToastType.SUCCESS});
    },
    onError: (error: AxiosError) => {
      queryClient.invalidateQueries({queryKey: ["app-settings"]}).then();
      Helpers.alertOnError(`Failed to update app settings. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
      setInFlight(false);
    },
  });

  function openTogglzConsole() {
    window.open(`${process.env.REACT_APP_CARECO_API_BASE_URL}/togglz_console/index`, '_blank');
  }

  function submit() {
    setInFlight(true);
    const updatedAppSettings = Object.values(form).map((setting: any) => setting.value);
    updateAppSettingsMutation.mutate(updatedAppSettings);
  }

  useEffect(() => {
    let formObject : any = {};
    appSettings?.forEach(setting => {
      formObject = {
        ...formObject,
        ...createFormFieldConfig(
          setting.name,
          setting,
          {valid: true, message: ''},
          [Validate.validateAppSettings],
          true
        )
      };
    });

    setInitialForm(formObject);
    setInFlight(false);
  }, [appSettings]);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>, appSetting : AppSetting) {
    const value = e.target.value;
    if (appSetting.dataType === AppSettingDataType.INTEGER) {
      if(!isNaN(Number(value))) {
        handleChange(e, appSetting.name, {...appSetting, value: Number(value).toString()});
      }
    } else {
      handleChange(e, appSetting.name, {...appSetting, value});
    }
  }

  function renderAppSettings(appSettingName: string) {
    const appSetting = form[appSettingName].value;

    if (appSetting?.dataType === AppSettingDataType.STRING || appSetting?.dataType === AppSettingDataType.INTEGER) {
      return (
        <ValidatedInput
          key={appSettingName}
          id={appSettingName}
          labelText={appSetting?.title}
          required={'required'}
          value={appSetting?.value}
          onChange={(e) => handleInputChange(e, appSetting)}
          validate={() => form[appSettingName]?.valid?.valid ? '' : form[appSettingName]?.valid?.message}
        />
      );
    }

    if (appSetting?.dataType === AppSettingDataType.BOOLEAN) {
      return (
        <Fragment key={appSetting.name}>
          <Checkbox
            key={appSettingName}
            id={appSettingName}
            value={appSetting.value === 'true'}
            onChange={(e) => handleChange(null, appSetting.name, {...appSetting, value: e.toString()})}
            label={appSetting.title}
          />
          <br/><br/>
        </Fragment>
      );
    }
  }

  return (
    <>
      <div className={css({display: 'flex', alignItems: 'center', marginBottom: '20px'})}>
        <SmallTypography>
          To dynamically manage application Feature Toggles visit:
        </SmallTypography>
        <Button styleType={StyleType.TERTIARY} onClick={openTogglzConsole}>
          Togglz Console
        </Button>
      </div>

      { isLoading ?
        <Loading iconSize={"4x"}/> :
        Object.keys(form).map(setting => renderAppSettings(setting))
      }

      <Button
        className={css({marginTop: '20px'})}
        type="submit"
        onClick={handleSubmit}
        disabled={!isValid() || !isFormChanged() || inFlight}
      >
        Submit
      </Button>

      <DeploymentFooter />
    </>
  );
}
